/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: UserActivityPage.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Main page for User Activity with list view and filters
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useEffect } from 'react';
import { User, RefreshCw, FilterX } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModulePageContainer } from '../../components/layouts';
import { useNavigationStore } from '../../store/navigation';
import { useUserActivityList } from './hooks/useUserActivityList';
import { UserActivity } from './types/userActivity.types';
import { UserActivityGrid } from './components/UserActivityGrid';
import { MetadataModal } from '../auditLogs/components/MetadataModal';
import { Button } from '../../components/ui/button';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import FixedActionBar from '../../components/ui/FixedActionBar';

export default function UserActivityPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const [selectedLog, setSelectedLog] = useState<UserActivity | null>(null);

  const pageTitle = useMemo(() => t('userActivity.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
  } = useUserActivityList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: UserActivity) => (
              <span className="text-sm text-foreground">
                {item.createdAt
                  ? new Date(item.createdAt).toLocaleString()
                  : '—'}
              </span>
            )
          };
        }
        if (column.key === 'action') {
          return {
            ...column,
            render: (_value: unknown, item: UserActivity) => {
              const actionColors: Record<string, string> = {
                CREATE: 'text-green-600 dark:text-green-400',
                UPDATE: 'text-blue-600 dark:text-blue-400',
                DELETE: 'text-red-600 dark:text-red-400',
                LOGIN: 'text-purple-600 dark:text-purple-400',
                LOGOUT: 'text-gray-600 dark:text-gray-400',
              };
              const colorClass = actionColors[item.action] || 'text-foreground';
              return (
                <span className={`text-sm font-medium ${colorClass}`}>
                  {item.action}
                </span>
              );
            }
          };
        }
        if (column.key === 'resource') {
          return {
            ...column,
            render: (_value: unknown, item: UserActivity) => (
              <span className="text-sm text-muted-foreground">
                {item.resource || '—'}
              </span>
            )
          };
        }
        if (column.key === 'ipAddress') {
          return {
            ...column,
            render: (_value: unknown, item: UserActivity) => (
              <span className="font-mono text-sm text-muted-foreground">
                {item.ipAddress || '—'}
              </span>
            )
          };
        }
        if (column.key === 'metadata') {
          return {
            ...column,
            render: (_value: unknown, item: UserActivity) => (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedLog(item)}
                className="h-8 text-xs"
              >
                {t('userActivity.viewMetadata')}
              </Button>
            )
          };
        }
        return column;
      }),
    [baseColumns, t]
  );

  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.startDate !== '' ||
      filters.endDate !== '' ||
      filters.actions.length > 0 ||
      filters.resource !== '' ||
      filters.domainId !== ''
    );
  }, [filters]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <div className="relative">
          <UserActivityGrid
            data={data}
            columns={columns}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filters={filters}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            onFilterChange={handleFilterChange}
          />

          {/* Fixed Action Bar - Always visible */}
          <FixedActionBar
            onReset={hasActiveFilters ? handleClearFilters : undefined}
            onSave={handleApplyFilters}
            resetText={
              <>
                <FilterX className="h-4 w-4 mr-2" />
                {t('userActivity.clearFilters')}
              </>
            }
            saveText={
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('userActivity.refresh')}
              </>
            }
            showCancel={false}
            showDelete={false}
            isSaving={loading}
          />

          {/* Metadata Modal */}
          {selectedLog && (
            <MetadataModal
              open={!!selectedLog}
              onClose={() => setSelectedLog(null)}
              metadata={selectedLog.metadata}
              action={selectedLog.action}
              resource={selectedLog.resource}
              timestamp={selectedLog.createdAt}
            />
          )}
        </div>
      }
      formView={<div />}
    />
  );
}

